// Customizable Area Start
import React from "react";
import { Box, Typography, styled, DialogContent, Avatar } from "@mui/material";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";
import CallEndRoundedIcon from "@mui/icons-material/CallEndRounded";
import CallModalController, { Props } from "./CallModalController.web";
import CustomDialogWrapper from "../../../components/src/CustomDialogWrapper.web";
import CustomIconButton from "../../../components/src/CustomIconButton.web";
import ConditionalRenderer from "../../../components/src/ConditionalRenderer.web";
import { formatCallTimer } from "../../../components/src/utils";
import MicLevelIcon from "../../../components/src/MicLevelIcon.web";
import VideoLevelIcon from "../../../components/src/VideoLevelIcon.web";
// Customizable Area End

export default class CallModal extends CallModalController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  // Customizable Area End

  render() {
    // Customizable Area Start
    const { isOpen } = this.props;
    const { cometCall } = this.state;

    if (!cometCall) {
      return null;
    }

    const { type, callReceiver } = cometCall;
    const isVideoCall = type === "video";
    const {
      isFullScreen,
      isCallStarted,
      callTimer,
      isMuted,
      isVideoOff,
    } = this.state;

    return (
      <>
        <CustomDialogWrapper
          open={isOpen}
          maxWidth={"sm"}
          fullScreen={isFullScreen}
        >
          <FullScreenIcon
            data-test-id="toggle-fullscreen"
            onClick={this.toggleFullScreen}
          >
            {isFullScreen ? (
              <FullscreenExitRoundedIcon data-test-id="exit-fullscreen" />
            ) : (
              <FullscreenRoundedIcon />
            )}
          </FullScreenIcon>
          <DialogContent
            sx={{ display: "flex", padding: "0", minWidth: "250px" }}
          >
            <ContentWrapper>
              <CallContentWrapper>
                <CallAvatar
                  src={callReceiver.avatar || ""}
                  alt={callReceiver.name}
                />
                <UserName>{callReceiver.name}</UserName>
                <CallStatus>
                  {isCallStarted ? formatCallTimer(callTimer) : "Ringing..."}
                </CallStatus>
              </CallContentWrapper>
              <VideoFrameWrapper
                isVideoCall={isVideoCall}
                isCallStarted={isCallStarted}
                ref={this.callContainerRef}
              />
              <CallControlsWrapper>
                <CustomIconButton
                  customVariant="primary"
                  data-test-id="call-mic-btn"
                  disabled={!isCallStarted}
                  onClick={this.toggleMute}
                >
                  <MicLevelIcon muted={isMuted} />
                </CustomIconButton>
                <ConditionalRenderer isVisible={isVideoCall}>
                  <CustomIconButton
                    data-test-id="call-video-btn"
                    customVariant="primary"
                    disabled={!isCallStarted}
                    onClick={this.toggleVideo}
                  >
                    <VideoLevelIcon data-test-id="video-icon" isOff={isVideoOff} />
                  </CustomIconButton>
                </ConditionalRenderer>
                <CustomIconButton
                  customVariant="error"
                  data-test-id="cancel-call"
                  onClick={this.handleEndCall}
                >
                  <CallEndRoundedIcon />
                </CustomIconButton>
              </CallControlsWrapper>
            </ContentWrapper>
          </DialogContent>
        </CustomDialogWrapper>
      </>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const ContentWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  overflow: "hidden",
  justifyContent: "center",
  alignItems: "center",
});

const CallStatus = styled(Typography)({
  color: "#64748B",
  fontSize: "0.875rem",
  lineHeight: "normal",
  fontWeight: 500,
});

const UserName = styled(Typography)({
  color: "#0F172A",
  fontWeight: 500,
  fontSize: "1rem",
  letterSpacing: "-0.15px",
  lineHeight: "19.5px",
});

const CallAvatar = styled(Avatar)({
  width: "70px",
  height: "70px",
  marginBottom: "2px",
  "& img": {
    objectPosition: "top",
  },
  "@media (max-width: 414px)": {
    height: "55px",
    width: "55px",
  },
  "@media (max-width: 850px)": {
    height: "65px",
    width: "65px",
  },
});

const CallContentWrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  gap: "3px",
  width: "100%",
  marginBottom: "15px",
});

const VideoFrameWrapper = styled(Box, {
  shouldForwardProp: (prop: string) =>
    !["isVideoCall", "isCallStarted"].includes(prop),
})<{ isVideoCall: boolean; isCallStarted: boolean }>(
  ({ isVideoCall, isCallStarted }) => ({
    width: isVideoCall && isCallStarted ? "100%" : "0",
    overflow: "hidden",
    borderRadius: "12px",
    aspectRatio: 16 / 9,
    transition: "all 0.3s ease-in-out",
    flex: 1,
    minHeight: "270px",
    display: isVideoCall && isCallStarted ? "block" : "none",
    "@media (max-width: 480px)": {
      aspectRatio: "4 / 3",
    },
  })
);

const CallControlsWrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  marginTop: "15px"
});

const FullScreenIcon = styled("button")({
  position: "absolute",
  backgroundColor: "transparent",
  outline: "none",
  border: "none",
  right: "16px",
  top: "16px",
  padding: "4px",
  borderRadius: "50%",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
  "& svg": {
    color: "#0F172A",
  },
});
// Customizable Area End
